import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
// Self-hosted Inter (Chunk 1 §2.3) — same-origin so it precaches + works
// offline, and drops the render-blocking Google Fonts request. Family name
// stays 'Inter' (the static @fontsource package), so --font-sans and every
// other reference are unchanged. Latin subset covers en/es/pt.
import '@fontsource/inter/latin-400.css'
import '@fontsource/inter/latin-500.css'
import '@fontsource/inter/latin-600.css'
import '@fontsource/inter/latin-700.css'
import './i18n'
import './index.css'
import App from './App'
import { getSupabase } from '@/lib/supabase'
import { useSettingsStore, applyInitialTextScale } from '@/lib/settings-store'
import { installReleaseCapabilities } from '@/lib/release-capabilities'
import { installAppUpdate } from '@/lib/app-update'
import { installContentEscrow } from '@/lib/content-escrow/install'
import { captureJobLocatorFromUrl } from '@/lib/keepsake/job-locator'

// Release capability tuple (PC-B rev 9 decision 5): created by the
// BUNDLED document JavaScript so the operator can prove which JS is
// running in every context during the R0-R6 cutover. Installed before
// React renders; Settings About displays the same in-memory tuple.
installReleaseCapabilities();

// Read-only protected-backup entry point. The former technical snapshot
// restore bridge is deliberately not installed in the one-account-per-browser
// release: neither a known different owner nor an unidentified profile can
// replace the browser's global content from the application shell.
installContentEscrow();

// S0 keepsake job locator (PC-B rev 9 10a.2): capture ?keepsakeJob=<uuid>
// into tab-scoped sessionStorage and strip it from browser history BEFORE
// React Router initializes.
// S0 validity-intent redispatch (PC-B rev 9 10b.2): pending intents
// dispatch by owner + numeric local sequence on boot and on reconnect.
//
// Capture is a local privacy boundary, not a feature-side effect. Keep it on
// even during maintenance/rollback so opaque job ids never remain in browser
// history or referrers and can be recovered after the feature reopens.
captureJobLocatorFromUrl();

// Apply the persisted body-text size preference to <html> before React
// renders so rem-based typography paints at the correct scale on the
// first frame instead of flashing from default → user's preference.
applyInitialTextScale();

// Boot Supabase early so detectSessionInUrl processes the OAuth hash
// before React Router navigates and strips it.
getSupabase();

// App-update scheduler: learns when this document is stale and reloads it
// only at a safe moment (never mid-typing, mid-recording, mid-save or during
// a sign-in return). Idempotent. Placed after getSupabase() so it never
// changes when the client is first created (rule #17 ordering stays as is).
installAppUpdate();

// Owner-bearing sync, hydration, audio processing, enrichment, and keepsake
// dispatch start only inside App's passed OwnerGate. Keeping them out of module
// boot prevents a signed-in different account from touching protected local
// state while the friendly account-safety screen is visible.

// Voice fallback wiring — when the device loses connectivity, enhanced voice
// can't reach our server so we flag 'offline' and useSpeechToText routes the
// next tap through browser-native Web Speech. Cleared on the online event.
// navigator.onLine is a best-effort signal (not 100% reliable across all
// networks) but works for the common "WiFi dropped" / "airplane mode" cases.
if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
  const { setFallbackReason } = useSettingsStore.getState();
  if (!navigator.onLine) {
    setFallbackReason('offline');
  }
  window.addEventListener('online', () => {
    // Only clear offline flag — don't touch a 'service' flag set by a
    // recent transcription failure (they're independent signals).
    if (useSettingsStore.getState().fallbackReason === 'offline') {
      useSettingsStore.getState().setFallbackReason(null);
    }
  });
  window.addEventListener('offline', () => {
    useSettingsStore.getState().setFallbackReason('offline');
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
